import { useEffect, useState } from 'preact/hooks';

import { useFilter } from '../../contexts/FilterContext';
import { dataService } from '../../functions/apis/dataService';
import type { PortfolioOverviewResponse } from '../../types/api';

export interface PortfolioOverviewState {
  data: PortfolioOverviewResponse | null;
  loading: boolean;
  error: string | null;
}

/**
 * The one shared request for all Overview sections.
 *
 * DataService caches the promise by client/currency, so each hook can consume
 * this safely without creating another request while the page is mounting.
 */
export function usePortfolioOverview(): PortfolioOverviewState {
  const { selectedClient } = useFilter();
  const [data, setData] = useState<PortfolioOverviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!selectedClient?.user_id_no) {
      setData(null);
      setLoading(false);
      setError(null);
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);
    setError(null);
    performance.mark('portfolio-overview-request-start');
    dataService
      .getPortfolioOverview(selectedClient.user_id_no)
      .then((response) => {
        if (!cancelled) {
          performance.mark('portfolio-overview-request-end');
          performance.measure(
            'portfolio-overview-request',
            'portfolio-overview-request-start',
            'portfolio-overview-request-end',
          );
          setData(response);
        }
      })
      .catch((loadError) => {
        if (!cancelled) {
          setData(null);
          setError(loadError instanceof Error ? loadError.message : 'Overview data could not be loaded.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedClient?.user_id_no]);

  return { data, loading, error };
}
